/**
 * Test an agent from Pliable: start one of its saved steps as if one of its triggers fired.
 * A connection's event takes the payload that connection would deliver (prefilled with a
 * sample, as JSON), so the trigger's filter and grouping see what they would for real; a
 * run by hand takes what to ask. It runs in the harness like any run (POST
 * /api/v1/agents/{id}/runs with `trigger`): its tools act for real, but the test has no
 * thread to reply to and never joins real work. It opens live (WorkItemSheet) once started.
 *
 * With `version` (one that isn't live), it tests that version instead, on copies of its steps
 * (POST /api/v1/agent-teams/{id}/versions/{n}/runs, teams.test_copy): their hand-offs stay in it.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import ApiService, { ApiError } from '@/services/api.service';
import { Button } from '@/components/ui/button';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { showToast } from '@/services/toast.service';
import { EVENTS, scheduleSample, triggerLabel, type Connection, type Member } from './catalog';

const api = () => ApiService.getInstance();
const errorText = (e: unknown, fallback: string) =>
  e instanceof ApiError ? ((e.payload as unknown as { detail?: string } | undefined)?.detail ?? fallback) : fallback;

type Started = { outcome: string; reason: string | null; runs: string[]; work_item_id: string };

export default function AgentTest({
  teamId,
  version,
  steps,
  connections,
  open,
  onOpenChange,
  onStarted,
}: {
  teamId: string;
  version?: number;
  steps: Member[];
  connections: Connection[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onStarted: (workItemId: string) => void;
}) {
  const queryClient = useQueryClient();
  const starts = steps.flatMap((m) =>
    m.triggers.map((t, i) => {
      const connector = connections.find((c) => c.name === t.connection)?.connector;
      const spec = connector ? EVENTS[connector]?.find((e) => e.event === t.event) : undefined;
      return {
        key: `${m.name}:${i}`,
        stepName: m.name,
        stepId: m.id!,
        trigger: i,
        byHand: t.source === 'user',
        sample: t.source === 'schedule' ? scheduleSample(t) : (spec?.sample ?? {}),
        label: `${m.name}: ${triggerLabel(t, spec?.label)}`,
      };
    }),
  );
  const [key, setKey] = useState('');
  const [edited, setText] = useState<string | null>(null); // null: the trigger's sample
  const start = starts.find((s) => s.key === key) ?? starts[0];
  const text = edited ?? (start && !start.byHand ? JSON.stringify(start.sample, null, 2) : '');
  const pick = (k: string) => {
    setKey(k);
    setText(null);
  };

  let payload: Record<string, unknown> | null = null;
  if (start?.byHand) payload = { input: text };
  else {
    try {
      const parsed = JSON.parse(text);
      payload = parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
    } catch {
      payload = null;
    }
  }

  const run = useMutation({
    mutationFn: async () =>
      (await api().request(
        'POST',
        version === undefined ? `/agents/${start!.stepId}/runs` : `/agent-teams/${teamId}/versions/${version}/runs`,
        { step: start!.stepName, trigger: start!.trigger, payload },
      )) as Started,
    onSuccess: (r) => {
      if (r.outcome !== 'routed') {
        showToast.error(r.reason ?? `Didn't start (${r.outcome})`);
        return;
      }
      queryClient.invalidateQueries({
        queryKey: ['agent-teams', teamId, 'work-items'],
      });
      showToast.success('Test started');
      onOpenChange(false);
      onStarted(r.work_item_id);
    },
    onError: (e) => showToast.error(errorText(e, 'Could not start the test.')),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{version === undefined ? 'Test this agent' : `Test version ${version}`}</DialogTitle>
          <DialogDescription>
            Starts the saved agent here, as if the trigger fired. Its tools act for real, but it has no thread to reply to.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          {start ? (
            <div className="space-y-4">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Start as</Label>
                <Select value={start.key} onValueChange={pick}>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {starts.map((s) => (
                      <SelectItem key={s.key} value={s.key}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">
                  {start.byHand ? 'What to ask' : 'The event, as it arrives (JSON)'}
                </Label>
                <Textarea
                  className={`min-h-[220px] ${start.byHand ? '' : 'font-mono text-xs'}`}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                />
                {!start.byHand && !payload && <p className="text-xs text-destructive">Not a JSON object.</p>}
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Nothing starts this agent yet. Add a trigger to a step (Steps → Starts when) and save.
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!start || !payload || (start.byHand && !text.trim()) || run.isPending} onClick={() => run.mutate()}>
            {run.isPending ? 'Starting…' : 'Run test'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
